<template>
  <div class="cohort-search-box">
    <div class="hvh-flex column align-center mx-3">
      <div class="w-50">
        <div class="font-xxl bold text-center">Find & Create Cohorts</div>
        <DPDSearchSingle
          class="mb-3"
          :categorySetting="categorySetting"
          :tags="cohortTags"
          placeholder="Search Name, Description, Creator, Tags, or Clinical Criteria"
          :value="searchText && { label: searchText }"
          @input="addSearchValue"
          @paste="itemPasted"
          ref="dpdSearch"
        />

        <div class="hvh-flex wrap pills" v-if="specialTags.any()">
          <CohortSearchPill class="pill-margin" :item="item" @remove="removeSpecialTag" v-for="item in specialTags" :key="`tag-${item}`" />
        </div>
        <div class="hvh-flex wrap pills" v-if="searchTags.any()">
          <CohortSearchPill class="pill-margin" :item="item" @remove="remove" v-for="item in searchTags" :key="`tag-${item}`" />
        </div>
        <div class="hvh-flex wrap pills" v-if="searchClientCriteria.any()">
          <CohortSearchPill class="pill-margin" :item="item" @click="copyItem(item)" @remove="remove" v-for="item in searchClientCriteria" :key="`criteria-${item.search}`" />
        </div>
      </div>
      <div class="position-relative w-100 hvh-flex center">
        <div class="horizontal-splitter hvh-flex wrap position-relative pt-3 mb-2" v-if="showFilters">
          <DateRangeFilter class="mr-3 mb-3" :filters="mergeFilter({ dateRange: null })" :value="filter.dateRange" @input="setFilter('dateRange', $event)" />
          <DatasetFilter class="mr-3 mb-3" :filters="mergeFilter({ dataset: null })" :value="filter.dataset" @input="setFilter('dataset', $event)" />
          <ClinicalCriteriaFilter class="mr-3 mb-3" :filters="mergeFilter({ clinicalCriteria: null })" :class="{ disabled: false && !search.any() }" :value="filter.clinicalCriteria" @input="setFilter('clinicalCriteria', $event)" />
          <CreatorFilter class="mr-3 mb-3" :filters="mergeFilter({ creator: null })" :value="filter.creator" @input="setFilter('creator', $event)" />
          <TagsFilter class="mr-3 mb-3" :filters="mergeFilter({ tags: null })" :value="filter.tags" @input="setFilter('tags', $event)" />
          <TypesFilter class="mr-3 mb-3" :filters="mergeFilter({ types: null })" :value="filter.types" @input="setFilter('types', $event)" />
          <AnalyticsFilter class="mr-3 mb-3" :filters="mergeFilter({ analytics: null })" :value="filter.analytics" @input="setFilter('analytics', $event)" />
        </div>
        <LoaderLinear v-if="isLoadingFilters || isLoading" />
      </div>
    </div>
    <div class="new-cohort" :class="{ show: searchClientCriteria.any() }" v-if="getFlag('cohorts-show-construct-new-cohort-button')">
      <div class="new-cohort-container text-center p-3 mt-4">
        <p class="font-sm mb-1">Don’t see a cohort that meets your needs?</p>
        <button @click="$emit('create-cohort')">1-Click New Cohort</button>
      </div>
    </div>
  </div>
</template>

<script>
import cloneDeep from 'clone-deep';
import axios from '../../../axios';
import { Debouncer, errorDebug, mergeFilters } from '../../../libraries/common';
import CreatorFilter from './searchFilters/CreatorFilter.vue';
import DatasetFilter from './searchFilters/DatasetFilter.vue';
import DateRangeFilter from './searchFilters/DateRangeFilter.vue';
import ClinicalCriteriaFilter from './searchFilters/ClinicalCriteriaFilter.vue';
import TagsFilter from './searchFilters/TagsFilter.vue';
import TypesFilter from './searchFilters/TypesFilter.vue';
import AnalyticsFilter from './searchFilters/AnalyticsFilter.vue';
import CohortSearchPill from './CohortSearchPill.vue';
import LoaderLinear from '../../common/loader/LoaderLinear.vue';
import DPDSearchSingle from '../../common/drugProdSelect/dpdSelect/DPDSearchSingle.vue';
import { getCriteriaCode } from '../../../libraries/criteria';
import { findDPDItem } from '../../../libraries/dpdSearch';
export default {
  components: { DateRangeFilter, ClinicalCriteriaFilter, TypesFilter, AnalyticsFilter, DatasetFilter, TagsFilter, CreatorFilter, CohortSearchPill, LoaderLinear, DPDSearchSingle },
  props: ['value', 'showFilters', 'isLoading'],
  data() {
    return {
      cohortTags: [],
      categorySetting: {
        drug: true,
        diagnose: true,
        procedure: true,
        physician: true,
      },
      isLoadingFilters: false,
      debouncer: new Debouncer(1000),
    };
  },
  computed: {
    searchText: {
      get() {
        return this.value.text;
      },
      set(value) {
        this.$emit('input', { ...this.value, text: value });
      }
    },
    search: {
      get() {
        return cloneDeep(this.value.search);
      },
      set(value) {
        this.$emit('input', { ...this.value, search: value });
      },
    },
    searchTags() {
      return this.search.filter(f => f.search.toLowerCase().startsWith('tag'));
    },
    specialTags() {
      return Object.keys(this.value.special || {})
        .filter(key => this.value.special[key])
        .map(f => f.splitByUpperCase().map(g => g.capitalize()).join(' '));
    },
    searchClientCriteria() {
      return this.search.filter(f => !this.searchTags.includes(f));
    },
    filter: {
      get() {
        return cloneDeep(this.value.filter);
      },
      set(value) {
        this.$emit('input', { ...this.value, filter: value });
      },
    },
  },
  methods: {
    getTags() {
      axios.get('/Analytics/Cohorts/Tags')
        .then(({ data }) => this.cohortTags = data)
        .catch(errorDebug);
    },
    addSearchValue(event) {
      if (!event) {
        this.searchText = null;
      } else if (event.id === 'searchWord') {
        this.searchText = event.label;
      } else {
        if (!findDPDItem(this.search, event.search))
          this.search = [...this.search, event];
        this.$nextTick(() => this.searchText = null);
      }
    },
    itemPasted(item) {
      if (findDPDItem(this.search, item.search)) {
        var dpdSearch = this.$refs.dpdSearch;
        dpdSearch.searchWordChanged(getCriteriaCode(item.label) || item.label);
        dpdSearch.openOptions();
      } else {
        this.addSearchValue(item);
      }
    },
    remove(item) {
      var hasFilters = Object.values(this.filter).any(f => f && ((Array.isArray(f) && f.any()) || !f.startsWith('All')));
      var shouldConfirm = hasFilters && this.search.length === 1;
      var promise = shouldConfirm && this.$confirm('Remove all filters', 'Are you sure you want to remove your current search results?');

      (promise || Promise.resolve())
        .then(() => this.$emit('input', {
          ...this.value,
          search: this.value.search.filter(f => item.chain ? f.chain !== item.chain : f.search !== item.search)
        }))
        .catch(() => null);
    },
    removeSpecialTag(event) {
      var tag = Object.keys(this.value.special).find(key => key.toLowerCase() === event.toLowerCase().replace(' ', ''));
      delete this.value.special[tag];
      this.$emit('input', { ...this.value });
    },
    setFilter(prop, event) {
      this.filter = { ...this.filter, [prop]: event };
    },
    mergeFilter(obj) {
      return mergeFilters(this.value, {
        filter: {
          ...this.value.filter,
          ...(obj || {})
        }
      });
    },
    copyItem(item) {
      localStorage.setItem('clipboard', JSON.stringify(item));
      navigator.clipboard.writeText(getCriteriaCode(item.label) || item.label)
        .then(() => this.$notify('Copied to clipboard!'))
    },
  },
  watch: {
    value(search) {
      this.searchText = search.text;
      if (!search.search.any() && !search.text) {
        this.value.filter = {};
      }
    },
  },
  mounted() {
    if (process.env.NODE_ENV === 'development') window['thisCohortSearchBox'] = this;
    this.getTags();
  },
}
</script>

<style lang="scss" scoped>
.cohort-search-box {
  .horizontal-splitter {
    border-top: 1px solid #d2d2d4;
  }

  .new-cohort {
    width: 100%;
    overflow: hidden;
    max-height: 0;
    transition: max-height 400ms ease 0ms;
    &.show {
      max-height: 100px;
    }

    .new-cohort-container {
      background-color: #f2f4f7;
      box-shadow: 0 3px 4px 0 rgba(4, 7, 23, 0.07);

      p {
        color: #4e6583;
      }
      button {
        padding: 8px 10px;
        border-radius: 7px;
        background-color: #666adf;
        color: white;
      }
    }
  }

  .pills {
    margin-bottom: 6px;
    .pill-margin {
      margin: 0 10px 10px 0;
    }
  }

  .vue-treeselect__option--disabled .vue-treeselect__label-container {
    color: rgba(0, 0, 0, 1);
    cursor: inherit;
  }
}
</style>

<style lang="scss">
.cohort-search-box {
  .loader {
    .progress {
      top: -3px;
    }
  }
}
</style>